import { buildDailyMetricTrends } from "../health/dailyHealthTrends";
import type { DailyHealthRecord, DailyHealthMetricName, DailyMetricTrend } from "../health/dailyHealthTypes";
import type { AthleteProfile, PreferredUnits } from "../athlete-profile/types/athleteProfile";
import type { AthleteTimelineEvent } from "../train/athleteTimeline";
import type { AthleteIntelligenceSnapshot } from "../train/athleteIntelligence";
import type { CompletedActivityRecord } from "../train/completedActivityHistory";
import {
  formatDistanceFromMeters,
  formatMinutesAsDuration,
  formatWeightFromKilograms
} from "../../lib/unitPreferences";

export type BriefingConfidence = "High" | "Medium" | "Low";
export type ReadinessStatus = "Excellent" | "Good" | "Moderate" | "Limited";
export type RecommendationKind = "Recovery Ride" | "Threshold Session" | "VO2 Intervals" | "Strength Training" | "Rest Day" | "Long Endurance Ride";
export type TrendIndicator = "up" | "down" | "flat" | "insufficient";

export type BriefingReason = {
  id: string;
  label: string;
  source: string;
};

export type ChecklistItem = {
  id: string;
  label: string;
  source: string;
};

export type SignalCard = {
  id: string;
  label: string;
  value: string;
  trend: TrendIndicator;
  detail: string;
};

export type DailyCoachBriefing = {
  athleteName: string;
  readinessScore: number;
  readinessStatus: ReadinessStatus;
  confidence: BriefingConfidence;
  confidenceExplanation: string;
  recommendation: {
    kind: RecommendationKind;
    explanation: string;
    estimatedDuration: string;
    confidence: BriefingConfidence;
  };
  reasons: BriefingReason[];
  checklist: ChecklistItem[];
  upcomingEvent: {
    name: string | null;
    date: string | null;
    countdownLabel: string;
    daysRemaining: number | null;
    configured: boolean;
  };
  currentSignals: SignalCard[];
  recentActivity: {
    available: boolean;
    activityId: string | null;
    title: string;
    rideType: string;
    distance: string;
    duration: string;
    normalizedPower: string;
    intensityFactor: string;
    emptyMessage?: string;
  };
  recoverySummary: {
    available: boolean;
    items: Array<{ label: string; value: string }>;
    emptyMessage?: string;
  };
};

export type DailyCoachBriefingInput = {
  athleteProfile: AthleteProfile;
  athleteIntelligence: AthleteIntelligenceSnapshot;
  activityHistoryRecords: CompletedActivityRecord[];
  dailyHealthRecords: DailyHealthRecord[];
  contextEvents: AthleteTimelineEvent[];
  preferredUnits?: PreferredUnits;
  nowIso?: string;
};

type ScoreContribution = {
  id: string;
  points: number;
  label: string;
  source: string;
};

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function round(value: number): number {
  return Math.round(value);
}

function hoursBetween(olderIso: string, newerIso: string): number | null {
  const older = toTimestamp(olderIso);
  const newer = toTimestamp(newerIso);
  if (older === null || newer === null) return null;
  return (newer - older) / (1000 * 60 * 60);
}

function daysBetween(futureIso: string, nowIso: string): number | null {
  const future = toTimestamp(futureIso);
  const now = toTimestamp(nowIso);
  if (future === null || now === null) return null;
  return Math.ceil((future - now) / (1000 * 60 * 60 * 24));
}

function classifyReadiness(score: number): ReadinessStatus {
  if (score >= 85) return "Excellent";
  if (score >= 70) return "Good";
  if (score >= 55) return "Moderate";
  return "Limited";
}

function classifyConfidence(availableSignals: number): BriefingConfidence {
  if (availableSignals >= 5) return "High";
  if (availableSignals >= 3) return "Medium";
  return "Low";
}

function latestRide(records: CompletedActivityRecord[]): CompletedActivityRecord | null {
  return [...records].sort((left, right) => Date.parse(right.activityDate) - Date.parse(left.activityDate))[0] ?? null;
}

function latestDailyHealth(records: DailyHealthRecord[]): DailyHealthRecord | null {
  return [...records].sort((left, right) => right.localDate.localeCompare(left.localDate))[0] ?? null;
}

function trendForMetric(records: DailyHealthRecord[], metric: DailyHealthMetricName): DailyMetricTrend | null {
  return buildDailyMetricTrends(records, metric).find(item => item.windowDays === 7) ?? null;
}

function trendIndicator(direction: DailyMetricTrend["direction"] | "stable" | "improving" | "declining"): TrendIndicator {
  if (direction === "above" || direction === "improving") return "up";
  if (direction === "below" || direction === "declining") return "down";
  if (direction === "within" || direction === "stable") return "flat";
  return "insufficient";
}

function isHardRide(record: CompletedActivityRecord): boolean {
  return (record.intensityFactor ?? 0) >= 0.85 || (record.trainingStressScore ?? 0) >= 85 || (record.durationSeconds ?? 0) >= 9000;
}

function isRecoveryRide(record: CompletedActivityRecord): boolean {
  return record.rideClassification.type === "recovery" || (record.intensityFactor ?? 1) <= 0.65;
}

function sevenDayLoad(records: CompletedActivityRecord[], nowIso: string): { tss: number; rideCount: number } {
  const now = toTimestamp(nowIso) ?? Date.now();
  const cutoff = now - (7 * 24 * 60 * 60 * 1000);
  const recent = records.filter(record => {
    const timestamp = toTimestamp(record.activityDate);
    return timestamp !== null && timestamp >= cutoff;
  });
  return {
    tss: recent.reduce((sum, record) => sum + (record.trainingStressScore ?? 0), 0),
    rideCount: recent.length
  };
}

function recentContextEvents(events: AthleteTimelineEvent[], nowIso: string, lookbackDays: number): AthleteTimelineEvent[] {
  const now = toTimestamp(nowIso) ?? Date.now();
  const cutoff = now - (lookbackDays * 24 * 60 * 60 * 1000);
  return events.filter(event => {
    const timestamp = toTimestamp(event.timestamp ?? event.canonicalTimestampIso ?? `${event.eventDate}T12:00:00.000Z`);
    return timestamp !== null && timestamp >= cutoff;
  });
}

function formatConfidenceExplanation(confidence: BriefingConfidence, availableSignals: number): string {
  if (confidence === "High") {
    return `${availableSignals} readiness inputs are available across rides, health, and context.`;
  }
  if (confidence === "Medium") {
    return `${availableSignals} readiness inputs are available; missing data reduces certainty, not readiness.`;
  }
  return "Recommendation confidence is limited because several recovery or training inputs are still missing.";
}

function upcomingEvent(profile: AthleteProfile, nowIso: string): DailyCoachBriefing["upcomingEvent"] {
  const configuredGoal = profile.goals.primaryEventName.trim() && profile.goals.primaryEventDate
    ? { name: profile.goals.primaryEventName.trim(), date: profile.goals.primaryEventDate }
    : null;

  const fallbackRace = [...profile.training.raceCalendar]
    .filter(race => race.name.trim() && race.date)
    .sort((left, right) => {
      if (left.priority !== right.priority) return left.priority.localeCompare(right.priority);
      return Date.parse(left.date ?? "") - Date.parse(right.date ?? "");
    })[0] ?? null;

  const next = configuredGoal ?? (fallbackRace ? { name: fallbackRace.name, date: fallbackRace.date } : null);
  if (!next?.date || !next.name) {
    return {
      name: null,
      date: null,
      countdownLabel: "No primary event configured.",
      daysRemaining: null,
      configured: false
    };
  }

  const daysRemaining = daysBetween(next.date, nowIso);
  return {
    name: next.name,
    date: next.date,
    countdownLabel: daysRemaining === null
      ? "Date unavailable"
      : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`,
    daysRemaining,
    configured: true
  };
}

function buildReadiness(input: DailyCoachBriefingInput, currentEvent: DailyCoachBriefing["upcomingEvent"]): {
  score: number;
  confidence: BriefingConfidence;
  confidenceExplanation: string;
  contributions: ScoreContribution[];
  latestRideRecord: CompletedActivityRecord | null;
  latestHealthRecord: DailyHealthRecord | null;
  hrvTrend: DailyMetricTrend | null;
  restingHrTrend: DailyMetricTrend | null;
  sleepTrend: DailyMetricTrend | null;
  recentTimeline: AthleteTimelineEvent[];
} {
  const preferredUnits = input.preferredUnits ?? input.athleteProfile.personal.preferredUnits;
  void preferredUnits;
  const latestRideRecord = latestRide(input.activityHistoryRecords);
  const latestHealthRecord = latestDailyHealth(input.dailyHealthRecords);
  const hrvTrend = trendForMetric(input.dailyHealthRecords, "hrvSdnnMs");
  const restingHrTrend = trendForMetric(input.dailyHealthRecords, "restingHeartRateBpm");
  const sleepTrend = trendForMetric(input.dailyHealthRecords, "sleepDurationMinutes");
  const recentTimeline = recentContextEvents(input.contextEvents, input.nowIso ?? new Date().toISOString(), 14);
  const contributions: ScoreContribution[] = [];
  let availableSignals = 0;

  if (latestHealthRecord && latestHealthRecord.sleep.totalSleepMinutes !== null) {
    availableSignals += 1;
    const targetMinutes = input.athleteProfile.recovery.preferredSleepHours * 60;
    const delta = latestHealthRecord.sleep.totalSleepMinutes - targetMinutes;
    let points = 0;
    if (delta >= 30) points = 10;
    else if (delta >= -30) points = 4;
    else if (delta <= -120) points = -12;
    else if (delta <= -60) points = -8;
    contributions.push({
      id: "sleep",
      points,
      label: delta === 0
        ? "Sleep matched target."
        : delta > 0
        ? `Sleep exceeded target by ${Math.round(delta)} minutes.`
        : `Sleep fell short of target by ${Math.abs(Math.round(delta))} minutes.`,
      source: "Apple Health local import"
    });
  }

  if (hrvTrend && hrvTrend.direction !== "insufficient") {
    availableSignals += 1;
    const points = hrvTrend.direction === "above" ? 8 : hrvTrend.direction === "within" ? 3 : -8;
    contributions.push({
      id: "hrv",
      points,
      label: `HRV is ${hrvTrend.language}.`,
      source: "Apple Health local import"
    });
  }

  if (restingHrTrend && restingHrTrend.direction !== "insufficient") {
    availableSignals += 1;
    const points = restingHrTrend.direction === "below" ? 8 : restingHrTrend.direction === "within" ? 3 : -8;
    contributions.push({
      id: "resting-hr",
      points,
      label: `Resting HR is ${restingHrTrend.language}.`,
      source: "Apple Health local import"
    });
  }

  if (latestRideRecord) {
    availableSignals += 1;
    if (isHardRide(latestRideRecord)) {
      contributions.push({
        id: "latest-ride-hard",
        points: -12,
        label: `Latest ride carried high load (${latestRideRecord.trainingStressScore ?? 0} TSS, IF ${latestRideRecord.intensityFactor?.toFixed(2) ?? "--"}).`,
        source: "Completed ride analysis"
      });
    } else if (isRecoveryRide(latestRideRecord)) {
      contributions.push({
        id: "latest-ride-recovery",
        points: 6,
        label: "Latest ride was a recovery-focused session.",
        source: "Completed ride analysis"
      });
    } else {
      contributions.push({
        id: "latest-ride-steady",
        points: -3,
        label: "Latest ride added moderate training load.",
        source: "Completed ride analysis"
      });
    }
  }

  const load = sevenDayLoad(input.activityHistoryRecords, input.nowIso ?? new Date().toISOString());
  if (load.rideCount > 0) {
    availableSignals += 1;
    const weeklyTarget = input.athleteProfile.training.weeklyTssTarget;
    if (load.tss >= weeklyTarget * 1.1) {
      contributions.push({
        id: "weekly-load-high",
        points: -8,
        label: `Last 7 days reached ${Math.round(load.tss)} TSS against a ${weeklyTarget} TSS target.`,
        source: "Completed ride analysis"
      });
    } else if (load.tss >= weeklyTarget * 0.7) {
      contributions.push({
        id: "weekly-load-on-track",
        points: -2,
        label: `Recent load is on track at ${Math.round(load.tss)} TSS this week.`,
        source: "Completed ride analysis"
      });
    } else {
      contributions.push({
        id: "weekly-load-light",
        points: 4,
        label: `Recent load is light at ${Math.round(load.tss)} TSS this week.`,
        source: "Completed ride analysis"
      });
    }
  }

  const blockers = recentTimeline.filter(event => ["injury", "injury_or_symptom", "illness", "travel"].includes(event.type ?? event.eventType ?? ""));
  if (recentTimeline.length > 0) {
    availableSignals += 1;
  }
  if (blockers.length > 0) {
    const blockerKinds = [...new Set(blockers.map(event => (event.type ?? event.eventType ?? "context").replaceAll("_", " ")))].join(", ");
    contributions.push({
      id: "timeline-blockers",
      points: blockers.some(event => (event.type ?? event.eventType) === "injury" || (event.type ?? event.eventType) === "injury_or_symptom" || (event.type ?? event.eventType) === "illness") ? -16 : -6,
      label: `Recent timeline context includes ${blockerKinds}.`,
      source: "Athlete Timeline"
    });
  } else if (recentTimeline.length > 0) {
    contributions.push({
      id: "timeline-clear",
      points: 3,
      label: "No recent injury, illness, or travel blockers were found.",
      source: "Athlete Timeline"
    });
  }

  if (currentEvent.configured && currentEvent.daysRemaining !== null && currentEvent.daysRemaining <= 90) {
    contributions.push({
      id: "event-near",
      points: 0,
      label: `${currentEvent.name} is ${currentEvent.daysRemaining} day${currentEvent.daysRemaining === 1 ? "" : "s"} away.`,
      source: "Athlete profile"
    });
  }

  const score = clamp(round(65 + contributions.reduce((sum, item) => sum + item.points, 0)), 0, 100);
  const confidence = classifyConfidence(availableSignals);
  return {
    score,
    confidence,
    confidenceExplanation: formatConfidenceExplanation(confidence, availableSignals),
    contributions,
    latestRideRecord,
    latestHealthRecord,
    hrvTrend,
    restingHrTrend,
    sleepTrend,
    recentTimeline
  };
}

function buildRecommendation(input: DailyCoachBriefingInput, readiness: ReturnType<typeof buildReadiness>, currentEvent: DailyCoachBriefing["upcomingEvent"]): DailyCoachBriefing["recommendation"] {
  const phase = input.athleteProfile.training.currentTrainingPhase.toLowerCase();
  const latestRideRecord = readiness.latestRideRecord;
  const recentBlockers = readiness.recentTimeline.filter(event => ["injury", "injury_or_symptom", "illness", "travel"].includes(event.type ?? event.eventType ?? ""));
  const eventSoon = currentEvent.daysRemaining !== null && currentEvent.daysRemaining <= 90;

  if (recentBlockers.some(event => ["injury", "injury_or_symptom", "illness"].includes(event.type ?? event.eventType ?? "")) || readiness.score < 45) {
    return {
      kind: "Rest Day",
      explanation: "Current context favors recovery over additional load today.",
      estimatedDuration: "20-30 min optional walk or mobility",
      confidence: readiness.confidence
    };
  }

  if (latestRideRecord && isHardRide(latestRideRecord) && readiness.score < 70) {
    return {
      kind: "Recovery Ride",
      explanation: "Yesterday's workload was already high, so today's best move is low-stress aerobic work.",
      estimatedDuration: "45-75 min",
      confidence: readiness.confidence
    };
  }

  if (phase.includes("recovery")) {
    return {
      kind: readiness.score >= 55 ? "Recovery Ride" : "Rest Day",
      explanation: "The athlete profile is set to a recovery phase, so intensity stays capped today.",
      estimatedDuration: readiness.score >= 55 ? "45-60 min" : "20-30 min optional walk or mobility",
      confidence: readiness.confidence
    };
  }

  if (readiness.score >= 86 && (phase.includes("peak") || eventSoon)) {
    return {
      kind: "VO2 Intervals",
      explanation: "Readiness is high enough to support a sharper stimulus close to the current race timeline.",
      estimatedDuration: "60-75 min",
      confidence: readiness.confidence
    };
  }

  if (readiness.score >= 78 && (phase.includes("build") || phase.includes("threshold"))) {
    return {
      kind: "Threshold Session",
      explanation: "Current readiness supports sustained quality work aligned with the training phase.",
      estimatedDuration: "75-90 min",
      confidence: readiness.confidence
    };
  }

  if (readiness.score >= 72) {
    return {
      kind: "Long Endurance Ride",
      explanation: "Steady aerobic work is the best fit for today's readiness and current training progression.",
      estimatedDuration: "120-180 min",
      confidence: readiness.confidence
    };
  }

  if (readiness.score >= 58) {
    return {
      kind: "Strength Training",
      explanation: "Readiness supports useful work today, but not the highest ride intensity on the board.",
      estimatedDuration: "35-50 min",
      confidence: readiness.confidence
    };
  }

  return {
    kind: "Recovery Ride",
    explanation: "Today's readiness is better served by low-intensity aerobic work than by accumulating more stress.",
    estimatedDuration: "45-60 min",
    confidence: readiness.confidence
  };
}

function buildReasons(input: DailyCoachBriefingInput, readiness: ReturnType<typeof buildReadiness>, recommendation: DailyCoachBriefing["recommendation"]): BriefingReason[] {
  const reasons = [...readiness.contributions]
    .sort((left, right) => Math.abs(right.points) - Math.abs(left.points))
    .map(item => ({ id: item.id, label: item.label, source: item.source }));

  reasons.push({
    id: "phase",
    label: `Training phase is ${input.athleteProfile.training.currentTrainingPhase}.`,
    source: "Athlete profile"
  });

  if (recommendation.kind === "Threshold Session" || recommendation.kind === "VO2 Intervals") {
    reasons.push({
      id: "progression",
      label: "Training plan progression supports a quality session today.",
      source: "Athlete profile"
    });
  }

  if (reasons.length < 3 && input.dailyHealthRecords.length === 0) {
    reasons.push({
      id: "missing-health",
      label: "No Apple Health recovery data is available yet, so readiness confidence is reduced.",
      source: "Apple Health local import"
    });
  }

  if (reasons.length < 3 && input.activityHistoryRecords.length === 0) {
    reasons.push({
      id: "missing-rides",
      label: "No completed rides are available yet, so training-load context is limited.",
      source: "Completed ride analysis"
    });
  }

  return reasons.slice(0, 6);
}

function buildChecklist(input: DailyCoachBriefingInput, readiness: ReturnType<typeof buildReadiness>, recommendation: DailyCoachBriefing["recommendation"], currentEvent: DailyCoachBriefing["upcomingEvent"]): ChecklistItem[] {
  const checklist: ChecklistItem[] = [];
  const rideRecommendation = ["Recovery Ride", "Threshold Session", "VO2 Intervals", "Long Endurance Ride"].includes(recommendation.kind);
  const recentTravel = readiness.recentTimeline.find(event => (event.type ?? event.eventType) === "travel");
  const recentEquipmentChange = readiness.recentTimeline.find(event => (event.type ?? event.eventType) === "equipment_change");

  if (rideRecommendation) {
    checklist.push({
      id: "bottles",
      label: `Mix bottles for ${input.athleteProfile.nutrition.preferredFluidsMlPerHour} ml/hr before the ride.`,
      source: "Athlete profile"
    });
    checklist.push({
      id: "fuel",
      label: `Prep ride fuel for ${input.athleteProfile.nutrition.preferredRideCarbsPerHour} g carbs/hr.`,
      source: "Athlete profile"
    });
  }

  if (input.athleteProfile.recovery.morningColdPlungeEnabled && (recommendation.kind === "Recovery Ride" || recommendation.kind === "Rest Day")) {
    checklist.push({
      id: "recovery-protocol",
      label: `Run your cold-plunge recovery protocol before training day decisions drift.`,
      source: "Athlete profile"
    });
  }

  if (input.athleteProfile.medicalAndConsiderations.currentMedications && input.athleteProfile.medicalAndConsiderations.currentMedications.length > 0) {
    checklist.push({
      id: "medications",
      label: "Review medication timing before training.",
      source: "Athlete profile"
    });
  }

  if (input.dailyHealthRecords.length === 0) {
    checklist.push({
      id: "apple-health",
      label: "Use Apple Health sleep tonight to improve tomorrow's readiness confidence.",
      source: "Apple Health local import"
    });
  }

  if (!currentEvent.configured) {
    checklist.push({
      id: "primary-event",
      label: "Configure a primary event so the daily briefing can prioritize progression against a target.",
      source: "Athlete profile"
    });
  }

  if (recentTravel) {
    checklist.push({
      id: "travel-context",
      label: "Account for recent travel before pushing intensity.",
      source: "Athlete Timeline"
    });
  }

  if (recentEquipmentChange) {
    checklist.push({
      id: "equipment-context",
      label: "Recheck the recent equipment change before the next session starts.",
      source: "Athlete Timeline"
    });
  }

  return checklist.slice(0, 6);
}

function signalDetailFromTrend(trend: DailyMetricTrend | null): { trend: TrendIndicator; detail: string } {
  if (!trend || trend.direction === "insufficient") {
    return { trend: "insufficient", detail: "Insufficient trend history." };
  }
  return {
    trend: trendIndicator(trend.direction),
    detail: trend.language.charAt(0).toUpperCase() + trend.language.slice(1)
  };
}

function buildCurrentSignals(input: DailyCoachBriefingInput, readiness: ReturnType<typeof buildReadiness>): SignalCard[] {
  const preferredUnits = input.preferredUnits ?? input.athleteProfile.personal.preferredUnits;
  const latestHealthRecord = readiness.latestHealthRecord;
  const bodyWeightTrend = trendForMetric(input.dailyHealthRecords, "bodyWeightKg");
  const stepsTrend = trendForMetric(input.dailyHealthRecords, "steps");
  const sleepDetail = signalDetailFromTrend(readiness.sleepTrend);
  const hrvDetail = signalDetailFromTrend(readiness.hrvTrend);
  const restingDetail = signalDetailFromTrend(readiness.restingHrTrend);
  const weightDetail = signalDetailFromTrend(bodyWeightTrend);
  const fitnessTrend = input.athleteIntelligence.rideCount >= 14 ? trendIndicator(input.athleteIntelligence.currentTrend) : "insufficient";

  return [
    {
      id: "sleep",
      label: "Sleep",
      value: latestHealthRecord?.sleep.totalSleepMinutes !== null && latestHealthRecord?.sleep.totalSleepMinutes !== undefined
        ? formatMinutesAsDuration(latestHealthRecord.sleep.totalSleepMinutes)
        : "No recovery data",
      trend: sleepDetail.trend,
      detail: sleepDetail.detail
    },
    {
      id: "recovery",
      label: "Recovery",
      value: `${classifyReadiness(readiness.score)} ${readiness.score}`,
      trend: readiness.score >= 70 ? "up" : readiness.score >= 55 ? "flat" : "down",
      detail: `Confidence ${readiness.confidence}`
    },
    {
      id: "fitness",
      label: "Fitness",
      value: input.athleteIntelligence.rideCount === 0 ? "No ride history" : `${input.athleteIntelligence.rideCount} rides analyzed`,
      trend: fitnessTrend,
      detail: fitnessTrend === "insufficient" ? "Insufficient trend history." : `${input.athleteIntelligence.currentTrend.charAt(0).toUpperCase()}${input.athleteIntelligence.currentTrend.slice(1)} trend.`
    },
    {
      id: "body-weight",
      label: "Body weight",
      value: latestHealthRecord?.bodyComposition.bodyWeightKg !== null && latestHealthRecord?.bodyComposition.bodyWeightKg !== undefined
        ? formatWeightFromKilograms(latestHealthRecord.bodyComposition.bodyWeightKg, preferredUnits)
        : "No body weight",
      trend: weightDetail.trend,
      detail: weightDetail.detail
    },
    {
      id: "hrv",
      label: "HRV",
      value: latestHealthRecord?.recovery.hrvSdnnMs !== null && latestHealthRecord?.recovery.hrvSdnnMs !== undefined
        ? `${Math.round(latestHealthRecord.recovery.hrvSdnnMs)} ms`
        : "No HRV",
      trend: hrvDetail.trend,
      detail: hrvDetail.detail
    },
    {
      id: "resting-hr",
      label: "Resting HR",
      value: latestHealthRecord?.recovery.restingHeartRateBpm !== null && latestHealthRecord?.recovery.restingHeartRateBpm !== undefined
        ? `${Math.round(latestHealthRecord.recovery.restingHeartRateBpm)} bpm`
        : "No resting HR",
      trend: restingDetail.trend,
      detail: restingDetail.detail
    },
    {
      id: "steps",
      label: "Steps",
      value: latestHealthRecord?.activityContext.steps !== null && latestHealthRecord?.activityContext.steps !== undefined
        ? `${Math.round(latestHealthRecord.activityContext.steps)}`
        : "No step data",
      trend: signalDetailFromTrend(stepsTrend).trend,
      detail: signalDetailFromTrend(stepsTrend).detail
    }
  ];
}

function buildRecentActivitySummary(input: DailyCoachBriefingInput): DailyCoachBriefing["recentActivity"] {
  const preferredUnits = input.preferredUnits ?? input.athleteProfile.personal.preferredUnits;
  const record = latestRide(input.activityHistoryRecords);
  if (!record) {
    return {
      available: false,
      activityId: null,
      title: "No completed rides yet",
      rideType: "Completed ride analysis will appear here once a ride is saved.",
      distance: "--",
      duration: "--",
      normalizedPower: "--",
      intensityFactor: "--",
      emptyMessage: "No completed rides have been analyzed yet. Finish one ride analysis to populate today's activity summary."
    };
  }

  return {
    available: true,
    activityId: record.activityId,
    title: record.activityTitle,
    rideType: record.rideClassification.displayName,
    distance: record.distanceMeters === null ? "--" : formatDistanceFromMeters(record.distanceMeters, preferredUnits),
    duration: record.durationSeconds === null ? "--" : formatMinutesAsDuration(record.durationSeconds / 60),
    normalizedPower: record.normalizedPower === null ? "--" : `${Math.round(record.normalizedPower)} W`,
    intensityFactor: record.intensityFactor === null ? "--" : record.intensityFactor.toFixed(2)
  };
}

function buildRecoverySummary(input: DailyCoachBriefingInput): DailyCoachBriefing["recoverySummary"] {
  const preferredUnits = input.preferredUnits ?? input.athleteProfile.personal.preferredUnits;
  const record = latestDailyHealth(input.dailyHealthRecords);
  if (!record) {
    return {
      available: false,
      items: [],
      emptyMessage: "No Apple Health recovery summary is available yet. Import local health data to unlock sleep, HRV, resting HR, and body-composition summaries."
    };
  }

  return {
    available: true,
    items: [
      { label: "Sleep", value: record.sleep.totalSleepMinutes === null ? "Not available" : formatMinutesAsDuration(record.sleep.totalSleepMinutes) },
      { label: "HRV", value: record.recovery.hrvSdnnMs === null ? "Not available" : `${Math.round(record.recovery.hrvSdnnMs)} ms` },
      { label: "Resting HR", value: record.recovery.restingHeartRateBpm === null ? "Not available" : `${Math.round(record.recovery.restingHeartRateBpm)} bpm` },
      { label: "Weight", value: record.bodyComposition.bodyWeightKg === null ? "Not available" : formatWeightFromKilograms(record.bodyComposition.bodyWeightKg, preferredUnits) },
      { label: "Body fat", value: record.bodyComposition.bodyFatPercentage === null ? "Not available" : `${record.bodyComposition.bodyFatPercentage.toFixed(1)}%` },
      { label: "Steps", value: record.activityContext.steps === null ? "Not available" : `${Math.round(record.activityContext.steps)}` }
    ]
  };
}

export function buildDailyCoachBriefing(input: DailyCoachBriefingInput): DailyCoachBriefing {
  const nowIso = input.nowIso ?? new Date().toISOString();
  const currentEvent = upcomingEvent(input.athleteProfile, nowIso);
  const readiness = buildReadiness({ ...input, nowIso }, currentEvent);
  const recommendation = buildRecommendation({ ...input, nowIso }, readiness, currentEvent);
  const checklist = buildChecklist({ ...input, nowIso }, readiness, recommendation, currentEvent);
  const reasons = buildReasons({ ...input, nowIso }, readiness, recommendation);

  return {
    athleteName: input.athleteProfile.personal.firstName,
    readinessScore: readiness.score,
    readinessStatus: classifyReadiness(readiness.score),
    confidence: readiness.confidence,
    confidenceExplanation: readiness.confidenceExplanation,
    recommendation,
    reasons,
    checklist,
    upcomingEvent: currentEvent,
    currentSignals: buildCurrentSignals({ ...input, nowIso }, readiness),
    recentActivity: buildRecentActivitySummary({ ...input, nowIso }),
    recoverySummary: buildRecoverySummary({ ...input, nowIso })
  };
}